import { Order, Payment } from '../services/dataService';

/**
 * Calculate order totals consistently across the application
 */
export const calculateOrderTotals = (items: Array<{ totalPrice: number }>) => {
  const subtotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
  const tax = 0; // No tax in this system
  const total = subtotal + tax;
  
  return {
    subtotal,
    tax,
    total
  };
};

/**
 * Calculate remaining amount and payment status consistently
 */
export const calculatePaymentStatus = (order: Order, payments?: Payment[]) => {
  const total = order.total || 0;
  
  // Use provided payments or order's paidAmount
  const totalPaid = payments 
    ? payments.reduce((sum, payment) => sum + (payment.amount || 0), 0)
    : (order.paidAmount || 0);
  
  const remainingAmount = Math.max(0, total - totalPaid);
  
  let paymentStatus: 'settled' | 'unsettled' | 'partial';
  if (remainingAmount <= 0) {
    paymentStatus = 'settled';
  } else if (totalPaid > 0) {
    paymentStatus = 'partial';
  } else {
    paymentStatus = 'unsettled';
  }
  
  const isPaid = remainingAmount <= 0;
  
  return {
    totalPaid,
    remainingAmount,
    paymentStatus,
    isPaid
  };
};

/**
 * Update order with new items and recalculate totals/payment status
 */
export const updateOrderWithItems = (
  order: Order, 
  newItems: any[], // Accept full OrderItem objects
  payments?: Payment[]
) => {
  const allItems = [...(order.items || []), ...newItems];
  const { subtotal, tax, total } = calculateOrderTotals(allItems);
  const { totalPaid, remainingAmount, isPaid } = calculatePaymentStatus(
    { ...order, total }, 
    payments
  );
  
  return {
    ...order,
    items: allItems,
    subtotal,
    tax,
    total,
    paidAmount: totalPaid,
    remainingAmount,
    isPaid
  };
};

/**
 * Remove items from order and recalculate totals/payment status
 */
export const removeItemsFromOrder = (
  order: Order,
  itemIdsToRemove: string[],
  payments?: Payment[]
) => {
  const filteredItems = (order.items || []).filter(item => !itemIdsToRemove.includes(item.id));
  const { subtotal, tax, total } = calculateOrderTotals(filteredItems);
  const { totalPaid, remainingAmount, isPaid } = calculatePaymentStatus(
    { ...order, total }, 
    payments
  );
  
  return {
    ...order,
    items: filteredItems,
    subtotal,
    tax,
    total,
    paidAmount: totalPaid,
    remainingAmount,
    isPaid
  };
}; 